import { ItemBar } from "components/ItemBar";
import { ToastContext } from "context/ToastContext";
import { prepareCodesForSubmit, CodeRangeError } from "helpers/codeRanges";
import { Button } from "primereact/button";
import { useContext } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  useApplyDiscountRulesetPropagationMutation,
  useLazyGetDiscountRulesetPropagationPreviewQuery,
  useUpdateDiscountRulesetGroupMutation,
  useUpdateDiscountRulesetMutation,
} from "store/queries/procedureDiscountRuleset";
import { RulesetApplicability } from "types/DefaultRules/DefaultRules";

import {
  formApplicability,
  mapFormToRequestBody,
} from "../helpers/mapFormToRequestBody";
import {
  scopeUpdateFields,
  shouldOfferPropagation,
} from "../helpers/ruleApplicability";
import { useRuleBucket } from "../hooks/useRuleBucket";
import { useSubmitRuleGroup } from "../hooks/useSubmitRuleGroup";
import { useRuleScopeComplete } from "./RuleApplicabilityField";
import { usePropagateAfterEdit } from "./usePropagateAfterEdit";

interface ISubmitEditProps {
  id: string;
  /**
   * CLE-2842 step 2 — the applicability the rule was loaded with. A save that converts the rule
   * (Payer-Provider ↔ Fallback) sends the new `applicability`; any other save sends none.
   */
  savedApplicability?: RulesetApplicability;
}

export function SubmitEdit({ id, savedApplicability }: ISubmitEditProps) {
  const {
    getValues,
    formState: { isValid },
  } = useFormContext();
  // CLE-2842 step 2 — a Payer-Provider rule needs its provider and payer, whatever the
  // scope fields' mount timing did to react-hook-form's own validity.
  const scopeComplete = useRuleScopeComplete();
  // CLE-2842 — a group save in flight blocks the page until it resolves.
  const saving = useRuleBucket()?.savingCount != null;
  const navigate = useNavigate();
  const [useUpdateDiscountRuleset] = useUpdateDiscountRulesetMutation();
  const [updateDiscountRulesetGroup] = useUpdateDiscountRulesetGroupMutation();
  // CLE-2842 — non-null when this rule is edited for every payer in its bucket at once.
  const submitGroup = useSubmitRuleGroup(updateDiscountRulesetGroup);

  const { current: toastElement } = useContext(ToastContext);

  const goBackToList = () => navigate("/payer-rules");

  const { openForId, modal } = usePropagateAfterEdit({
    useLazyPreview: useLazyGetDiscountRulesetPropagationPreviewQuery as any,
    useApplyPropagation: useApplyDiscountRulesetPropagationMutation as any,
    onDone: goBackToList,
  });

  const updatePayerRule = async () => {
    const values = getValues();
    let cptCodes: string[];
    try {
      cptCodes = prepareCodesForSubmit(values.cptCodes ?? []);
    } catch (err) {
      toastElement?.show({
        severity: "error",
        summary: "Invalid code range",
        detail:
          err instanceof CodeRangeError
            ? err.message
            : "Failed to expand code ranges",
      });
      return;
    }
    const requestBody = mapFormToRequestBody({ ...values, cptCodes });
    const applicability = formApplicability(values);
    if (submitGroup) {
      await submitGroup(requestBody);
      return;
    }
    try {
      useUpdateDiscountRuleset({
        ...requestBody,
        ...scopeUpdateFields(applicability, savedApplicability),
        id,
      })
        .unwrap()
        .then(() => {
          toastElement?.show({
            severity: "success",
            summary: "Payer Ruleset updated",
            detail: "Payer Ruleset updated successfully",
          });
          // Only a rule that was already Typical or Fallback has copies; a conversion returns
          // to the list.
          if (shouldOfferPropagation(applicability, savedApplicability)) {
            openForId(id);
          } else {
            goBackToList();
          }
        })
        .catch(() =>
          toastElement?.show({
            severity: "error",
            summary: "Error",
            detail:
              "An error has occurred attempting to update the discount ruleset",
          })
        );
    } catch (error) {
      console.error(error);
    }
  };
  return (
    <ItemBar>
      <div
        data-testid="submit__Hospital"
        className="flex flex-1 justify-content-end"
      >
        <Button
          label="Update Payer Rule"
          onClick={updatePayerRule}
          disabled={!isValid || !scopeComplete || saving}
          loading={saving}
        />
      </div>
      {modal}
    </ItemBar>
  );
}
